import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router";
import { Upload, ChevronDown, ChevronRight, CheckCircle2 } from "lucide-react";
import { parseCsv } from "../../lib/csv/parser";
import { mapRows } from "../../lib/csv/mapRows";
import { dedupeRows } from "../../lib/csv/dedupe";
import { suggestCategory } from "../../lib/csv/categorize";
import { normalizeVendor } from "../../lib/csv/normalizeVendor";
import type {
  ColumnMapping,
  CsvEntity,
  CsvFieldKey,
  MappedRow,
  ParsedCsv,
} from "../../lib/csv/types";
import { useExpenses } from "../../lib/expenses/useExpenses";
import { useInvoices } from "../../lib/invoices/useInvoices";
import { useCategories } from "../../lib/categories/useCategories";
import type { NewExpenseInput } from "../../lib/expenses/types";
import type { NewInvoiceInput } from "../../lib/invoices/types";
import { trackEvent } from "../../lib/telemetry/analytics";

type Step = 1 | 2 | 3 | 4 | 5;

type PresetId = "expensify" | "quickbooks" | "ramp" | "brex" | "chase" | "amex";
interface PlatformPreset {
  id: PresetId;
  label: string;
  columnMap: Partial<Record<string, CsvFieldKey>>;
}
const PLATFORM_PRESETS: PlatformPreset[] = [
  {
    id: "expensify",
    label: "Expensify",
    columnMap: {
      Date: "date",
      Merchant: "counterparty",
      "Original Amount": "amount",
      "Business Purpose": "notes",
    },
  },
  {
    id: "quickbooks",
    label: "QuickBooks",
    columnMap: {
      Date: "date",
      "Vendor/Customer": "counterparty",
      Amount: "amount",
      Memo: "notes",
    },
  },
  {
    id: "ramp",
    label: "Ramp",
    columnMap: {
      Date: "date",
      "Merchant Name": "counterparty",
      "Amount (Billed)": "amount",
      Memo: "notes",
    },
  },
  {
    id: "brex",
    label: "Brex",
    columnMap: {
      Date: "date",
      Vendor: "counterparty",
      Amount: "amount",
      Memo: "notes",
    },
  },
  {
    id: "chase",
    label: "Chase",
    columnMap: {
      "Transaction Date": "date",
      Description: "counterparty",
      Amount: "amount",
    },
  },
  {
    id: "amex",
    label: "Amex",
    columnMap: {
      Date: "date",
      Description: "counterparty",
      Amount: "amount",
    },
  },
];

// -------- header auto-mapping ------------------------------------------------

const MAP_PATTERNS: Array<{ pattern: RegExp; field: CsvFieldKey }> = [
  { pattern: /^date$/i, field: "date" },
  { pattern: /invoice.date/i, field: "date" },
  { pattern: /occurred/i, field: "date" },
  { pattern: /vendor|merchant|counterparty|description/i, field: "counterparty" },
  { pattern: /amount|total|price/i, field: "amount" },
  { pattern: /direction|type|flow/i, field: "direction" },
  { pattern: /notes?|memo/i, field: "notes" },
];

function autoMap(headers: string[], entity: CsvEntity): CsvFieldKey[] {
  // Match each header against the pattern table in order, first winner takes
  // it. Entity scoping: `direction` only matters when we're importing
  // invoices — for expenses we demote the match to `ignore` so the user
  // doesn't get a column they can't map anyway.
  return headers.map((h) => {
    for (const { pattern, field } of MAP_PATTERNS) {
      if (!pattern.test(h)) continue;
      if (field === "direction" && entity !== "invoice") return "ignore";
      return field;
    }
    return "ignore";
  });
}

function validateMapping(
  mapping: CsvFieldKey[],
  entity: CsvEntity,
): string | null {
  const counts = new Map<CsvFieldKey, number>();
  for (const m of mapping) counts.set(m, (counts.get(m) ?? 0) + 1);
  if (!counts.get("date")) return "Map at least one column to Date.";
  if (!counts.get("counterparty"))
    return "Map at least one column to Counterparty.";
  if (!counts.get("amount")) return "Map at least one column to Amount.";
  if (entity === "invoice" && (counts.get("direction") ?? 0) > 1) {
    return "At most one column can be mapped to Direction.";
  }
  return null;
}

// -------- component ----------------------------------------------------------

export function Import() {
  const navigate = useNavigate();
  const { expenses, addMany: addManyExpenses } = useExpenses();
  const { invoices, addMany: addManyInvoices } = useInvoices();
  const { categories } = useCategories();

  const [step, setStep] = useState<Step>(1);
  const [fileName, setFileName] = useState<string>("");
  const [parsed, setParsed] = useState<ParsedCsv | null>(null);
  const [parseError, setParseError] = useState<string | null>(null);
  const [entity, setEntity] = useState<CsvEntity>("expense");
  const [mapping, setMapping] = useState<CsvFieldKey[]>([]);
  const [autoMapped, setAutoMapped] = useState(false);

  // Preview-stage state.
  const [mapErrors, setMapErrors] = useState<
    Array<{ rowIndex: number; reason: string }>
  >([]);
  const [duplicates, setDuplicates] = useState<MappedRow[]>([]);
  const [fresh, setFresh] = useState<MappedRow[]>([]);

  const [selected, setSelected] = useState<Record<number, boolean>>({});
  const [rowCategoryIds, setRowCategoryIds] = useState<Record<number, string>>(
    {},
  );
  const [defaultCategoryId, setDefaultCategoryId] = useState<string>("");

  const [openErrors, setOpenErrors] = useState(true);
  const [openDuplicates, setOpenDuplicates] = useState(false);
  const [openFresh, setOpenFresh] = useState(true);

  const [rawCategories, setRawCategories] = useState<string[]>([]);
  const [categoryRemap, setCategoryRemap] = useState<Record<string, string>>({});

  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const [activePreset, setActivePreset] = useState<PresetId | null>(null);
  const [importedCount, setImportedCount] = useState(0);

  // Default category defaults to the first non-"uncategorized" entry so the
  // dropdown lands on a useful pre-selection. Falls back to the first item.
  useEffect(() => {
    if (defaultCategoryId || categories.length === 0) return;
    const preferred =
      categories.find((c) => c.code !== "uncategorized") ?? categories[0]!;
    setDefaultCategoryId(preferred.id);
  }, [categories, defaultCategoryId]);

  // -------- step 1: upload ---------------------------------------------------

  async function handleFile(file: File): Promise<void> {
    setParseError(null);
    setFileName(file.name);
    try {
      const text = await file.text();
      const result = parseCsv(text);
      if (result.headers.length === 0 || result.rows.length === 0) {
        setParseError("This file has no rows.");
        setParsed(null);
        return;
      }
      setParsed(result);
      setAutoMapped(false);
    } catch (err) {
      setParseError(err instanceof Error ? err.message : String(err));
      setParsed(null);
    }
  }

  // -------- step 3: auto-map on entry ---------------------------------------

  useEffect(() => {
    if (step !== 3 || !parsed || autoMapped) return;
    setMapping(autoMap(parsed.headers, entity));
    setAutoMapped(true);
  }, [step, parsed, entity, autoMapped]);

  const mappingError = useMemo(
    () => (parsed ? validateMapping(mapping, entity) : "Upload a file first."),
    [mapping, entity, parsed],
  );

  // -------- step 4: compute preview -----------------------------------------

  useEffect(() => {
    let cancelled = false;
    if (step !== 4 || !parsed) return;

    async function run() {
      const colMap: ColumnMapping = { mappings: mapping };
      const result = await mapRows(parsed!, colMap, entity);
      if (cancelled) return;
      setMapErrors(result.errors);

      const existingForDedupe =
        entity === "expense"
          ? expenses.map((e) => ({
              occurredOn: e.occurredOn,
              amountCents: e.amountCents,
              vendor: e.vendor,
            }))
          : invoices.map((i) => ({
              occurredOn: i.issuedOn,
              amountCents: i.amountCents,
              vendor: i.counterparty,
            }));

      const deduped = await dedupeRows(result.rows, existingForDedupe);
      if (cancelled) return;

      setDuplicates(deduped.duplicates);
      setFresh(deduped.fresh);

      // Extract unique raw categories if a category column is mapped
      const catColIndex = mapping.indexOf("category");
      if (catColIndex >= 0 && entity === "expense") {
        const rawCats = new Set<string>();
        for (const row of parsed!.rows) {
          const val = row[catColIndex]?.trim();
          if (val) rawCats.add(val);
        }
        setRawCategories([...rawCats]);
      } else {
        setRawCategories([]);
      }

      // Seed per-row checkbox + category state keyed by raw CSV row index,
      // which is stable across re-renders of this effect.
      const nextSelected: Record<number, boolean> = {};
      const nextCats: Record<number, string> = {};
      for (const row of deduped.fresh) {
        nextSelected[row.rawIndex] = true;
        if (entity === "expense") {
          const suggestion = suggestCategory({
            vendorNormalized: normalizeVendor(row.counterparty),
            existingExpenses: expenses
              .filter((e) => e.categoryId)
              .map((e) => ({
                vendor: e.vendor,
                categoryId: e.categoryId!,
              })),
          });
          if (suggestion) nextCats[row.rawIndex] = suggestion;
        }
      }
      setSelected(nextSelected);
      setRowCategoryIds(nextCats);
    }

    void run();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step, parsed, mapping, entity]);

  // -------- step 4: submit ---------------------------------------------------

  async function handleSubmit(): Promise<void> {
    setSubmitting(true);
    setSubmitError(null);
    try {
      const chosen = fresh.filter((row) => selected[row.rawIndex]);
      if (entity === "expense") {
        const inputs: NewExpenseInput[] = chosen.map((row) => {
          const catColIndex = mapping.indexOf("category");
          let catId = rowCategoryIds[row.rawIndex] || defaultCategoryId || null;
          if (catColIndex >= 0) {
            const rawCat = parsed!.rows[row.rawIndex]?.[catColIndex]?.trim();
            if (rawCat && categoryRemap[rawCat]) catId = categoryRemap[rawCat];
          }
          const cat = catId
            ? categories.find((c) => c.id === catId) ?? null
            : null;
          const input: NewExpenseInput = {
            vendor: row.counterparty,
            occurredOn: row.date,
            amountCents: row.amountCents,
            currency: "USD",
            categoryId: cat ? cat.id : null,
            categoryLabel: cat ? cat.label : null,
          };
          if (row.notes) input.businessPurpose = row.notes;
          return input;
        });
        await addManyExpenses(inputs);
      } else {
        const inputs: NewInvoiceInput[] = chosen.map((row) => ({
          counterparty: row.counterparty,
          direction: row.direction ?? "incoming",
          amountCents: row.amountCents,
          currency: "USD",
          issuedOn: row.date,
          status: "pending",
          notes: row.notes,
        }));
        await addManyInvoices(inputs);
      }

      trackEvent("csv_import_completed", {
        entity,
        rowCount: chosen.length,
      });

      setImportedCount(chosen.length);
      setStep(5);
    } catch (err) {
      setSubmitError(err instanceof Error ? err.message : String(err));
    } finally {
      setSubmitting(false);
    }
  }

  // -------- preset apply ----------------------------------------------------

  function applyPreset(preset: PlatformPreset): void {
    if (!parsed) return;
    const newMapping: CsvFieldKey[] = parsed.headers.map(
      (h) => preset.columnMap[h] ?? "ignore",
    );
    setMapping(newMapping);
    setAutoMapped(true);
    setActivePreset(preset.id);
    setEntity("expense");
    setStep(3);
  }

  // -------- derived --------------------------------------------------------

  const previewRows = parsed ? parsed.rows.slice(0, 10) : [];
  const fieldOptions: CsvFieldKey[] =
    entity === "invoice"
      ? ["ignore", "date", "counterparty", "amount", "direction", "notes"]
      : ["ignore", "date", "counterparty", "amount", "notes", "category"];
  const fieldLabel: Record<CsvFieldKey, string> = {
    ignore: "— Ignore —",
    date: "Date",
    counterparty: entity === "invoice" ? "Counterparty" : "Vendor",
    amount: "Amount",
    direction: "Direction",
    notes: "Notes",
    category: "Category",
  };

  const selectedCount = fresh.filter((r) => selected[r.rawIndex]).length;

  // -------- render ---------------------------------------------------------

  return (
    <div className="max-w-4xl mx-auto flex flex-col gap-6">
      <div>
        <h1 className="text-2xl font-bold text-slate-900">Import CSV</h1>
        <p className="text-slate-500 mt-1">
          Bring in expenses or invoices from a spreadsheet export.
        </p>
      </div>

      <Stepper step={step} />

      {step === 1 && (
        <section
          data-testid="import-step-1"
          className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6 flex flex-col gap-4"
        >
          <label className="flex flex-col gap-2 cursor-pointer">
            <span className="text-sm font-semibold text-slate-700">
              Choose a file (.csv, .tsv, .txt)
            </span>
            <div className="flex items-center gap-3 px-4 py-3 border border-dashed border-slate-300 rounded-xl bg-slate-50 hover:bg-slate-100">
              <Upload className="w-5 h-5 text-slate-500" />
              <span className="text-sm text-slate-700">
                {fileName || "Click to select a file"}
              </span>
              <input
                data-testid="import-file-input"
                type="file"
                accept=".csv,.tsv,.txt"
                className="sr-only"
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  if (f) void handleFile(f);
                }}
              />
            </div>
          </label>

          {parseError && (
            <div className="text-sm text-red-600" data-testid="import-error">
              {parseError}
            </div>
          )}

          {parsed && (
            <div className="overflow-x-auto border border-slate-200 rounded-xl">
              <table className="w-full text-left text-sm">
                <thead className="bg-slate-50 text-xs uppercase text-slate-500">
                  <tr>
                    {parsed.headers.map((h, i) => (
                      <th key={i} className="px-3 py-2 font-semibold">
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {previewRows.map((row, i) => (
                    <tr key={i}>
                      {row.map((cell, j) => (
                        <td key={j} className="px-3 py-2 text-slate-700">
                          {cell}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {parsed && (
            <div className="border-t border-slate-100 pt-4">
              <p className="text-sm font-medium text-slate-700 mb-2">
                Migrating from another tool?
              </p>
              <div className="flex flex-wrap gap-2">
                {PLATFORM_PRESETS.map((p) => (
                  <button
                    key={p.id}
                    type="button"
                    onClick={() => applyPreset(p)}
                    className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition-colors ${
                      activePreset === p.id
                        ? "bg-[#0070F2] text-white border-[#0070F2]"
                        : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50"
                    }`}
                  >
                    {p.label}
                  </button>
                ))}
              </div>
            </div>
          )}

          <div className="flex justify-end">
            <button
              type="button"
              onClick={() => setStep(2)}
              disabled={!parsed}
              className="px-4 py-2 bg-[#0070F2] hover:bg-[#005BCA] disabled:bg-slate-300 disabled:cursor-not-allowed text-white font-medium rounded-xl text-sm"
            >
              Next
            </button>
          </div>
        </section>
      )}

      {step === 2 && (
        <section
          data-testid="import-step-2"
          className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6 flex flex-col gap-4"
        >
          <h2 className="text-base font-semibold text-slate-800">
            What are these rows?
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <EntityChoice
              testid="import-entity-expenses"
              label="These are expenses"
              description="Go into the Inbox for review, default status needs_info."
              active={entity === "expense"}
              onClick={() => setEntity("expense")}
            />
            <EntityChoice
              testid="import-entity-invoices"
              label="These are invoices"
              description="Saved with status pending. Incoming or outgoing."
              active={entity === "invoice"}
              onClick={() => setEntity("invoice")}
            />
          </div>

          <div className="flex justify-between">
            <button
              type="button"
              onClick={() => setStep(1)}
              className="px-4 py-2 text-slate-600 bg-white border border-slate-200 hover:bg-slate-50 font-medium rounded-xl text-sm"
            >
              Back
            </button>
            <button
              type="button"
              onClick={() => setStep(3)}
              className="px-4 py-2 bg-[#0070F2] hover:bg-[#005BCA] text-white font-medium rounded-xl text-sm"
            >
              Next
            </button>
          </div>
        </section>
      )}

      {step === 3 && parsed && (
        <section
          data-testid="import-step-3"
          className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6 flex flex-col gap-4"
        >
          <div>
            <h2 className="text-base font-semibold text-slate-800">
              Map your columns
            </h2>
            <p className="text-sm text-slate-500 mt-1">
              We've guessed based on the header row. Tweak as needed.
            </p>
          </div>

          <div className="flex flex-col gap-2">
            {parsed.headers.map((header, i) => (
              <div
                key={i}
                className="flex items-center gap-3 px-3 py-2 border border-slate-200 rounded-lg"
              >
                <div className="flex-1 text-sm font-medium text-slate-700 truncate">
                  {header}
                </div>
                <select
                  data-testid={`import-map-select-${i}`}
                  value={mapping[i] ?? "ignore"}
                  onChange={(e) => {
                    const next = mapping.slice();
                    next[i] = e.target.value as CsvFieldKey;
                    setMapping(next);
                  }}
                  className="px-3 py-1.5 text-sm bg-white border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-[#0070F2]/30"
                >
                  {fieldOptions.map((opt) => (
                    <option key={opt} value={opt}>
                      {fieldLabel[opt]}
                    </option>
                  ))}
                </select>
              </div>
            ))}
          </div>

          {mappingError && (
            <div className="text-sm text-amber-700">{mappingError}</div>
          )}

          <div className="flex justify-between">
            <button
              type="button"
              onClick={() => setStep(2)}
              className="px-4 py-2 text-slate-600 bg-white border border-slate-200 hover:bg-slate-50 font-medium rounded-xl text-sm"
            >
              Back
            </button>
            <button
              type="button"
              onClick={() => setStep(4)}
              disabled={mappingError !== null}
              className="px-4 py-2 bg-[#0070F2] hover:bg-[#005BCA] disabled:bg-slate-300 disabled:cursor-not-allowed text-white font-medium rounded-xl text-sm"
            >
              Next
            </button>
          </div>
        </section>
      )}

      {step === 4 && (
        <section
          data-testid="import-step-4"
          className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6 flex flex-col gap-4"
        >
          <CollapsibleSection
            testid="import-preview-errors"
            title={`Errors (${mapErrors.length})`}
            open={openErrors}
            onToggle={() => setOpenErrors((v) => !v)}
          >
            {mapErrors.length === 0 ? (
              <div className="text-sm text-slate-500">No errors.</div>
            ) : (
              <ul className="text-sm text-red-700 list-disc list-inside space-y-1">
                {mapErrors.map((e, i) => (
                  <li key={i}>
                    Row {e.rowIndex + 2}: {e.reason}
                  </li>
                ))}
              </ul>
            )}
          </CollapsibleSection>

          <CollapsibleSection
            testid="import-preview-duplicates"
            title={`Duplicates (${duplicates.length}, skipped)`}
            open={openDuplicates}
            onToggle={() => setOpenDuplicates((v) => !v)}
          >
            {duplicates.length === 0 ? (
              <div className="text-sm text-slate-500">No duplicates.</div>
            ) : (
              <ul className="text-sm text-slate-600 space-y-1">
                {duplicates.map((row) => (
                  <li key={row.rawIndex}>
                    {row.date} — {row.counterparty} —{" "}
                    {formatCents(row.amountCents)}
                  </li>
                ))}
              </ul>
            )}
          </CollapsibleSection>

          {entity === "expense" && rawCategories.length > 0 && (
            <div className="border border-slate-200 rounded-xl p-4 flex flex-col gap-3">
              <div>
                <div className="text-sm font-semibold text-slate-800">Map imported categories</div>
                <p className="text-xs text-slate-500 mt-0.5">
                  Match each category from your file to a Proofly category.
                </p>
              </div>
              {rawCategories.map((raw) => (
                <div key={raw} className="flex items-center gap-3">
                  <span className="flex-1 text-sm text-slate-700 truncate font-medium">{raw}</span>
                  <span className="text-slate-400 text-xs">→</span>
                  <select
                    value={categoryRemap[raw] ?? ""}
                    onChange={(e) =>
                      setCategoryRemap((prev) => ({ ...prev, [raw]: e.target.value }))
                    }
                    className="px-3 py-1.5 text-sm bg-white border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-[#0070F2]/30"
                  >
                    <option value="">— Skip —</option>
                    {categories.map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.label}
                      </option>
                    ))}
                  </select>
                </div>
              ))}
            </div>
          )}

          <CollapsibleSection
            testid="import-preview-fresh"
            title={`Fresh (${fresh.length}, will be imported)`}
            open={openFresh}
            onToggle={() => setOpenFresh((v) => !v)}
          >
            {entity === "expense" && (
              <div className="flex items-center gap-3 mb-3">
                <label className="text-sm text-slate-700">
                  Default category for un-suggested rows
                </label>
                <select
                  data-testid="import-default-category"
                  value={defaultCategoryId}
                  onChange={(e) => setDefaultCategoryId(e.target.value)}
                  className="px-3 py-1.5 text-sm bg-white border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-[#0070F2]/30"
                >
                  {categories.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.label}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {fresh.length === 0 ? (
              <div className="text-sm text-slate-500">
                No rows ready to import.
              </div>
            ) : (
              <div className="overflow-x-auto border border-slate-200 rounded-lg">
                <table className="w-full text-left text-sm">
                  <thead className="bg-slate-50 text-xs uppercase text-slate-500">
                    <tr>
                      <th className="px-3 py-2 w-8"></th>
                      <th className="px-3 py-2">Date</th>
                      <th className="px-3 py-2">
                        {entity === "invoice" ? "Counterparty" : "Vendor"}
                      </th>
                      <th className="px-3 py-2">Amount</th>
                      {entity === "invoice" && (
                        <th className="px-3 py-2">Direction</th>
                      )}
                      {entity === "expense" && (
                        <th className="px-3 py-2">Category</th>
                      )}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {fresh.map((row) => {
                      const isChecked = selected[row.rawIndex] ?? true;
                      const catId =
                        rowCategoryIds[row.rawIndex] || defaultCategoryId;
                      return (
                        <tr
                          key={row.rawIndex}
                          data-testid={`import-preview-row-${row.rawIndex}`}
                        >
                          <td className="px-3 py-2">
                            <input
                              type="checkbox"
                              checked={isChecked}
                              onChange={(e) =>
                                setSelected((prev) => ({
                                  ...prev,
                                  [row.rawIndex]: e.target.checked,
                                }))
                              }
                            />
                          </td>
                          <td className="px-3 py-2 text-slate-700">
                            {row.date}
                          </td>
                          <td className="px-3 py-2 text-slate-700">
                            {row.counterparty}
                          </td>
                          <td className="px-3 py-2 text-slate-700">
                            {formatCents(row.amountCents)}
                          </td>
                          {entity === "invoice" && (
                            <td className="px-3 py-2 text-slate-700">
                              {row.direction ?? "incoming"}
                            </td>
                          )}
                          {entity === "expense" && (
                            <td className="px-3 py-2">
                              <select
                                data-testid={`import-preview-row-category-${row.rawIndex}`}
                                value={catId}
                                onChange={(e) =>
                                  setRowCategoryIds((prev) => ({
                                    ...prev,
                                    [row.rawIndex]: e.target.value,
                                  }))
                                }
                                className="px-2 py-1 text-sm bg-white border border-slate-200 rounded-md"
                              >
                                {categories.map((c) => (
                                  <option key={c.id} value={c.id}>
                                    {c.label}
                                  </option>
                                ))}
                              </select>
                            </td>
                          )}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </CollapsibleSection>

          {submitError && (
            <div
              data-testid="import-error"
              className="text-sm text-red-600 border border-red-200 bg-red-50 rounded-lg px-3 py-2"
            >
              {submitError}
            </div>
          )}

          <div className="flex justify-between items-center">
            <button
              type="button"
              onClick={() => setStep(3)}
              disabled={submitting}
              className="px-4 py-2 text-slate-600 bg-white border border-slate-200 hover:bg-slate-50 font-medium rounded-xl text-sm disabled:opacity-50"
            >
              Back
            </button>
            <div className="flex items-center gap-3">
              <span className="text-sm text-slate-500">
                {selectedCount} selected
              </span>
              <button
                type="button"
                data-testid="import-submit"
                onClick={() => void handleSubmit()}
                disabled={submitting || selectedCount === 0}
                className="px-4 py-2 bg-[#0070F2] hover:bg-[#005BCA] disabled:bg-slate-300 disabled:cursor-not-allowed text-white font-medium rounded-xl text-sm"
              >
                {submitting
                  ? "Importing..."
                  : `Import ${selectedCount} row${selectedCount === 1 ? "" : "s"}`}
              </button>
            </div>
          </div>
        </section>
      )}
      {step === 5 && (
        <section
          data-testid="import-step-5"
          className="bg-white rounded-2xl shadow-sm border border-slate-200 p-8 flex flex-col items-center gap-6 text-center"
        >
          <div className="w-14 h-14 rounded-full bg-green-50 border border-green-100 flex items-center justify-center">
            <CheckCircle2 className="w-7 h-7 text-green-600" />
          </div>
          <div>
            <h2 className="text-xl font-bold text-slate-900">
              {importedCount}{" "}
              {entity === "expense" ? "expense" : "invoice"}
              {importedCount !== 1 ? "s" : ""} imported
            </h2>
            <p className="text-slate-500 mt-1">
              Your records are in Proofly. Here's what to do next.
            </p>
          </div>
          <div className="w-full max-w-sm bg-amber-50 border border-amber-100 rounded-xl p-4 text-sm text-amber-800 text-left">
            <strong>Tip:</strong> Attach receipts to imported expenses from the
            Inbox to maximize your audit score.
          </div>
          <div className="flex gap-3">
            <button
              type="button"
              onClick={() => navigate("/audit")}
              className="px-5 py-2.5 bg-[#0070F2] hover:bg-[#005BCA] text-white font-medium rounded-xl text-sm"
            >
              View Audit Score
            </button>
            <button
              type="button"
              onClick={() => navigate("/inbox")}
              className="px-5 py-2.5 bg-white border border-slate-200 hover:bg-slate-50 text-slate-700 font-medium rounded-xl text-sm"
            >
              Go to Inbox
            </button>
          </div>
        </section>
      )}
    </div>
  );
}

// -------- subcomponents -----------------------------------------------------

function Stepper({ step }: { step: Step }) {
  const labels = ["Upload", "Entity", "Map", "Preview", "Done"];
  return (
    <div className="flex items-center gap-2">
      {labels.map((label, i) => {
        const n = i + 1;
        const active = step === n;
        const done = step > n;
        return (
          <div
            key={label}
            className={`flex-1 px-3 py-2 rounded-lg text-xs font-medium border ${
              active
                ? "bg-blue-50 border-blue-200 text-blue-700"
                : done
                  ? "bg-green-50 border-green-200 text-green-700"
                  : "bg-white border-slate-200 text-slate-500"
            }`}
          >
            {n}. {label}
          </div>
        );
      })}
    </div>
  );
}

function EntityChoice({
  testid,
  label,
  description,
  active,
  onClick,
}: {
  testid: string;
  label: string;
  description: string;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      data-testid={testid}
      onClick={onClick}
      aria-pressed={active}
      className={`text-left px-4 py-3 rounded-xl border transition-colors ${
        active
          ? "border-[#0070F2] bg-blue-50"
          : "border-slate-200 bg-white hover:bg-slate-50"
      }`}
    >
      <div className="text-sm font-semibold text-slate-800">{label}</div>
      <div className="text-xs text-slate-500 mt-1">{description}</div>
    </button>
  );
}

function CollapsibleSection({
  testid,
  title,
  open,
  onToggle,
  children,
}: {
  testid: string;
  title: string;
  open: boolean;
  onToggle: () => void;
  children: React.ReactNode;
}) {
  return (
    <div
      data-testid={testid}
      className="border border-slate-200 rounded-xl overflow-hidden"
    >
      <button
        type="button"
        onClick={onToggle}
        className="flex items-center gap-2 w-full px-4 py-3 text-left text-sm font-semibold text-slate-700 bg-slate-50 hover:bg-slate-100"
      >
        {open ? (
          <ChevronDown className="w-4 h-4" />
        ) : (
          <ChevronRight className="w-4 h-4" />
        )}
        {title}
      </button>
      {open && <div className="px-4 py-3 bg-white">{children}</div>}
    </div>
  );
}

function formatCents(cents: number): string {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
  }).format(cents / 100);
}
